/**
 * 親注文の**一括確認シート**（FR-128・D-83）＝A-03 の親カードから開く印刷ビュー。
 *
 * ★**正式な帳票ではない**＝卸をまたぐ「正式書類」を作ると、発行主体が存在しない紙になる
 *   （納品書・請求書は子注文＝卸ごとに出る＝FR-45/49）。だから**紙面に必ず断り書きを刷る**。
 * ★**保存しない・サーバに作らない**＝クライアントで組み立てて `window.print()` に渡すだけ。
 *   API を足すと「誰がいつ出したか」を残す話（監査）が付いてくるが、これは職人が自分の
 *   注文を見るための控えで、卸にも運営にも関係しない（親は職人側だけの概念＝D-85）。
 * ★**印刷は body 直下のポータルへ描いて他を隠す**＝新しいウィンドウを開く方式は、LINE の
 *   アプリ内ブラウザ（LIFF）でポップアップが開かないことがあり、そこで詰む。
 *   画面上はプレビューとして読めるので、印刷できない端末でも中身は確認できる。
 */

import type { CheckoutSession, PartnerOrder } from "@kenzai/api-client";
import { formatDate, formatDateTime, Money } from "@kenzai/ui";
import { createPortal } from "react-dom";

export function ParentSheet({
  parent,
  orders,
  onClose,
}: {
  parent: CheckoutSession;
  orders: PartnerOrder[];
  onClose: () => void;
}) {
  const missing = parent.child_refs.filter((r) => !orders.some((o) => o.order_id === r.order_id));

  return createPortal(
    <div
      data-kz-print
      data-testid="parent-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="一括確認シート"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        overflow: "auto",
        background: "var(--kz-color-surface)",
        padding: "var(--kz-space-4)",
      }}
    >
      {/* 印刷時はアプリ本体（#root）を隠し、この紙だけを流し込む。 */}
      <style>{`
        @media print {
          body > #root { display: none !important; }
          [data-kz-print] { position: static !important; overflow: visible !important; padding: 0 !important; }
          [data-kz-print] .kz-sheet-actions { display: none !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>

      <div className="kz-sheet-actions" style={{ display: "flex", gap: "var(--kz-space-2)", marginBottom: "var(--kz-space-4)" }}>
        <button
          type="button"
          data-testid="sheet-print"
          onClick={() => window.print()}
          className="kz-pressable"
          style={{
            minHeight: 44,
            padding: "0 var(--kz-space-4)",
            border: "none",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-primary)",
            color: "var(--kz-color-ink-inverse)",
            font: "inherit",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          印刷する
        </button>
        <button
          type="button"
          data-testid="sheet-close"
          onClick={onClose}
          className="kz-pressable"
          style={{
            minHeight: 44,
            padding: "0 var(--kz-space-4)",
            border: "1px solid var(--kz-color-border)",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-surface)",
            font: "inherit",
            cursor: "pointer",
          }}
        >
          閉じる
        </button>
      </div>

      <h1 style={{ fontSize: "var(--kz-font-size-lg)", margin: 0 }}>ご注文の控え（参考資料）</h1>
      <p style={{ margin: "var(--kz-space-2) 0", fontSize: "var(--kz-font-size-sm)" }}>
        まとめ番号 <span className="kz-num">{parent.display_no}</span> ／{" "}
        <span className="kz-num">{formatDateTime(parent.created_at)}</span> 受付
      </p>

      {orders.map((o) => (
        <section key={o.order_id} style={{ marginTop: "var(--kz-space-4)" }}>
          <h2 style={{ fontSize: "var(--kz-font-size-md)", margin: 0 }}>{o.tenant_name}</h2>
          <p style={{ margin: "2px 0", fontSize: "var(--kz-font-size-sm)" }}>
            受付番号 <span className="kz-num">{o.display_no}</span> ／ お届け先 {o.site_name ?? "確認中"} ／
            お届け予定 {formatDate(o.desired_date)}
            {o.bin_name && ` ${o.bin_name}`}
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--kz-font-size-sm)" }}>
            <thead>
              <tr>
                <th style={th}>品名</th>
                <th style={{ ...th, textAlign: "right" }}>数量</th>
                <th style={{ ...th, textAlign: "right" }}>金額</th>
              </tr>
            </thead>
            <tbody>
              {o.items.map((it, i) => (
                <tr key={i}>
                  <td style={td}>
                    {it.name}
                    {it.spec && `（${it.spec}）`}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <span className="kz-num">{Number(it.qty ?? 0)}</span>
                    {it.unit}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <Money value={it.amount} />
                  </td>
                </tr>
              ))}
              {o.delivery_fee !== null && (
                <tr>
                  <td style={td} colSpan={2}>
                    配送料
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <Money value={o.delivery_fee} />
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ ...td, fontWeight: 700 }} colSpan={2}>
                  小計
                </td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>
                  <Money
                    value={
                      o.total_amount === null
                        ? null
                        : String(Number(o.total_amount) + Number(o.delivery_fee ?? 0))
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      ))}

      {missing.length > 0 && (
        <p style={{ marginTop: "var(--kz-space-3)", fontSize: "var(--kz-font-size-sm)" }}>
          ※ 表示できないご注文が <span className="kz-num">{missing.length}</span> 件あります
          （お取引が終了した取引先のご注文）。合計はそれらを含みます。
        </p>
      )}

      <div
        style={{
          marginTop: "var(--kz-space-4)",
          paddingTop: "var(--kz-space-3)",
          borderTop: "2px solid var(--kz-color-border)",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 700,
        }}
      >
        <span>合計</span>
        <Money value={parent.total_amount} />
      </div>

      {/* FR-128：**必ず刷り込む**（この紙を納品書・請求書として使わせない） */}
      <p style={{ marginTop: "var(--kz-space-4)", fontSize: "var(--kz-font-size-sm)" }}>
        本書は参考資料です。正式な帳票は各お取引先の納品書・請求書をご確認ください。
      </p>
    </div>,
    document.body,
  );
}

const th = {
  textAlign: "left",
  borderBottom: "1px solid var(--kz-color-border)",
  padding: "4px 0",
  color: "var(--kz-color-ink-muted)",
  fontWeight: 600,
} as const;

const td = { borderBottom: "1px solid var(--kz-color-surface-sunken)", padding: "4px 0" } as const;
